import { useState } from "react";
import { AutomaticDiscountModal } from "@/components/admin/automatic-discount-modal";
import { AdminAvatar } from "@/components/admin/avatar";
import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { FlashSaleModal } from "@/components/admin/flash-sale-modal";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Pagination } from "@/components/admin/pagination";
import { PromotionModal } from "@/components/admin/promotion-modal";
import { Tag } from "@/components/admin/tag";
import type {
	AutomaticDiscount,
	DynamicAutoStatus,
} from "@/lib/promotions/automatic-discounts-api";
import {
	useAutomaticDiscountKpis,
	useAutomaticDiscountsList,
	useToggleAutomaticDiscountStatus,
} from "@/lib/promotions/automatic-discounts-hooks";
import type {
	DynamicFlashStatus,
	FlashSale,
} from "@/lib/promotions/flash-sales-api";
import {
	useDeleteFlashSale,
	useFlashSaleKpis,
	useFlashSalesList,
	useToggleFlashSaleStatus,
} from "@/lib/promotions/flash-sales-hooks";
import type {
	Coupon,
	DynamicPromoStatus,
} from "@/lib/promotions/promotions-api";
import {
	usePromotionKpis,
	usePromotionsList,
	useTogglePromotionStatus,
} from "@/lib/promotions/promotions-hooks";
import type { TagClass } from "@/lib/status";

function computeDynamicStatus(c: Coupon): {
	label: string;
	cls: TagClass;
} {
	if (!c.isActive) return { label: "Inactif", cls: "neutral" };
	const now = new Date();
	if (c.startsAt && new Date(c.startsAt) > now)
		return { label: "Programmé", cls: "info" };
	if (c.expiresAt && new Date(c.expiresAt) < now)
		return { label: "Expiré", cls: "warning" };
	if (c.maxRedemptions !== null && c.redeemedCount >= c.maxRedemptions)
		return { label: "Épuisé", cls: "terra" };
	return { label: "Actif", cls: "success" };
}

function computeAutoDynamicStatus(d: AutomaticDiscount): {
	label: string;
	cls: TagClass;
} {
	if (!d.isActive) return { label: "Inactif", cls: "neutral" };
	const now = new Date();
	if (d.startsAt && new Date(d.startsAt) > now)
		return { label: "Programmé", cls: "info" };
	if (d.expiresAt && new Date(d.expiresAt) < now)
		return { label: "Expiré", cls: "warning" };
	return { label: "Actif", cls: "success" };
}

function computeFlashDynamicStatus(fs: FlashSale): {
	label: string;
	cls: TagClass;
} {
	if (!fs.isActive) return { label: "Inactif", cls: "neutral" };
	const now = new Date();
	if (new Date(fs.startsAt) > now) return { label: "Programmé", cls: "info" };
	if (new Date(fs.expiresAt) < now) return { label: "Expiré", cls: "warning" };
	if (fs.stockLimit > 0 && fs.soldCount >= fs.stockLimit)
		return { label: "Épuisé", cls: "terra" };
	return { label: "En cours", cls: "success" };
}

function formatPeriod(
	startsAt: string | null,
	expiresAt: string | null,
): string {
	if (!startsAt && !expiresAt) return "Permanent";

	const formatFrenchDate = (dStr: string) => {
		try {
			const d = new Date(dStr);
			return d.toLocaleDateString("fr-FR", {
				day: "2-digit",
				month: "short",
				year: "numeric",
			});
		} catch {
			return dStr;
		}
	};

	const startFormatted = startsAt ? formatFrenchDate(startsAt) : null;
	const endFormatted = expiresAt ? formatFrenchDate(expiresAt) : null;

	if (startFormatted && endFormatted) {
		return `${startFormatted} au ${endFormatted}`;
	}
	if (startFormatted) return `Dès le ${startFormatted}`;
	return `Jusqu'au ${endFormatted}`;
}

export function AdminPromotionsPage() {
	const [activeModuleTab, setActiveModuleTab] = useState<
		"coupons" | "automatic" | "flash"
	>("coupons");

	// ÉTATS CODES PROMO (6.1 & 6.2)
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<DynamicPromoStatus | "">("");
	const [page, setPage] = useState(1);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);

	const { data: kpis } = usePromotionKpis();
	const { data, isLoading, isError, refetch } = usePromotionsList({
		search,
		status: statusFilter || undefined,
		page,
		limit: 20,
	});
	const toggleStatusMutation = useTogglePromotionStatus();

	// ÉTATS REMISES AUTOMATIQUES (6.3)
	const [autoSearch, setAutoSearch] = useState("");
	const [autoStatusFilter, setAutoStatusFilter] = useState<
		DynamicAutoStatus | ""
	>("");
	const [autoPage, setAutoPage] = useState(1);
	const [isAutoModalOpen, setIsAutoModalOpen] = useState(false);
	const [editingAutoDiscount, setEditingAutoDiscount] =
		useState<AutomaticDiscount | null>(null);

	const { data: autoKpis } = useAutomaticDiscountKpis();
	const {
		data: autoData,
		isLoading: isAutoLoading,
		isError: isAutoError,
		refetch: refetchAuto,
	} = useAutomaticDiscountsList({
		search: autoSearch,
		status: autoStatusFilter || undefined,
		page: autoPage,
		limit: 20,
	});
	const toggleAutoStatusMutation = useToggleAutomaticDiscountStatus();

	// ÉTATS VENTES FLASH PROGRAMMÉES (6.4)
	const [flashSearch, setFlashSearch] = useState("");
	const [flashStatusFilter, setFlashStatusFilter] = useState<
		DynamicFlashStatus | ""
	>("");
	const [flashPage, setFlashPage] = useState(1);
	const [isFlashModalOpen, setIsFlashModalOpen] = useState(false);
	const [editingFlashSale, setEditingFlashSale] = useState<FlashSale | null>(
		null,
	);

	const { data: flashKpis } = useFlashSaleKpis();
	const {
		data: flashData,
		isLoading: isFlashLoading,
		isError: isFlashError,
		refetch: refetchFlash,
	} = useFlashSalesList({
		search: flashSearch,
		status: flashStatusFilter || undefined,
		page: flashPage,
		limit: 20,
	});
	const toggleFlashStatusMutation = useToggleFlashSaleStatus();
	const deleteFlashMutation = useDeleteFlashSale();

	const handleOpenCreate = () => {
		setEditingCoupon(null);
		setIsModalOpen(true);
	};

	const handleOpenEdit = (coupon: Coupon) => {
		setEditingCoupon(coupon);
		setIsModalOpen(true);
	};

	const handleToggleStatus = (coupon: Coupon) => {
		toggleStatusMutation.mutate({ id: coupon.id, isActive: !coupon.isActive });
	};

	const handleOpenCreateAuto = () => {
		setEditingAutoDiscount(null);
		setIsAutoModalOpen(true);
	};

	const handleOpenEditAuto = (discount: AutomaticDiscount) => {
		setEditingAutoDiscount(discount);
		setIsAutoModalOpen(true);
	};

	const handleToggleAutoStatus = (discount: AutomaticDiscount) => {
		toggleAutoStatusMutation.mutate({
			id: discount.id,
			isActive: !discount.isActive,
		});
	};

	const handleOpenCreateFlash = () => {
		setEditingFlashSale(null);
		setIsFlashModalOpen(true);
	};

	const handleOpenEditFlash = (flashSale: FlashSale) => {
		setEditingFlashSale(flashSale);
		setIsFlashModalOpen(true);
	};

	const handleToggleFlashStatus = (flashSale: FlashSale) => {
		toggleFlashStatusMutation.mutate({
			id: flashSale.id,
			isActive: !flashSale.isActive,
		});
	};

	const [copiedCode, setCopiedCode] = useState<string | null>(null);

	const handleDeleteFlash = (id: string) => {
		if (confirm("Voulez-vous vraiment supprimer cette vente flash ?")) {
			deleteFlashMutation.mutate(id);
		}
	};

	const copyCode = (code: string) => {
		void navigator.clipboard.writeText(code);
		setCopiedCode(code);
		setTimeout(() => setCopiedCode(null), 2000);
	};

	const activeCount = kpis?.activeCount ?? 0;
	const redemptionsCount = kpis?.totalRedemptions30d ?? 0;
	const discountCount = kpis?.totalDiscountDistributed ?? 0;
	const totalCoupons = kpis?.totalCount ?? 0;

	return (
		<>
			<PageHead
				title="Promotions & Ventes Flash"
				sub="Gestion commerciale des codes promo, remises automatiques au panier et ventes flash programmées"
				actions={
					activeModuleTab === "coupons" ? (
						<AdminButton variant="primary" onClick={handleOpenCreate}>
							<AdminIcon n="plus" s={16} />
							Nouveau code promo
						</AdminButton>
					) : activeModuleTab === "automatic" ? (
						<AdminButton variant="primary" onClick={handleOpenCreateAuto}>
							<AdminIcon n="plus" s={16} />
							Nouvelle remise automatique
						</AdminButton>
					) : (
						<AdminButton variant="primary" onClick={handleOpenCreateFlash}>
							<AdminIcon n="plus" s={16} />
							Programmer une Vente Flash
						</AdminButton>
					)
				}
			/>

			{/* Navigation des sous-modules (Codes Promo / Remises Auto / Ventes Flash) */}
			<div
				style={{
					display: "inline-flex",
					alignItems: "center",
					gap: 3,
					padding: 3,
					background: "var(--a-surface-2)",
					border: "1px solid var(--a-border)",
					borderRadius: 9,
					marginBottom: 20,
					maxWidth: "100%",
					overflowX: "auto",
				}}
			>
				<button
					type="button"
					onClick={() => setActiveModuleTab("coupons")}
					className="active:scale-[0.96]"
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
						padding: "7px 14px",
						borderRadius: 6,
						fontSize: 12.5,
						fontWeight: activeModuleTab === "coupons" ? 600 : 500,
						cursor: "pointer",
						border:
							activeModuleTab === "coupons"
								? "1px solid var(--a-border)"
								: "1px solid transparent",
						transition:
							"color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
						background:
							activeModuleTab === "coupons"
								? "var(--a-surface)"
								: "transparent",
						color:
							activeModuleTab === "coupons"
								? "var(--a-ink)"
								: "var(--a-ink-mute)",
						boxShadow:
							activeModuleTab === "coupons" ? "var(--a-shadow-sm)" : "none",
						whiteSpace: "nowrap",
					}}
				>
					<AdminIcon
						n="tag"
						s={14}
						c={
							activeModuleTab === "coupons"
								? "var(--a-terracotta)"
								: "var(--a-ink-mute)"
						}
					/>
					<span className="tn">
						Codes Promo ({kpis?.activeCount ?? 0} actifs)
					</span>
				</button>

				<button
					type="button"
					onClick={() => setActiveModuleTab("automatic")}
					className="active:scale-[0.96]"
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
						padding: "7px 14px",
						borderRadius: 6,
						fontSize: 12.5,
						fontWeight: activeModuleTab === "automatic" ? 600 : 500,
						cursor: "pointer",
						border:
							activeModuleTab === "automatic"
								? "1px solid var(--a-border)"
								: "1px solid transparent",
						transition:
							"color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
						background:
							activeModuleTab === "automatic"
								? "var(--a-surface)"
								: "transparent",
						color:
							activeModuleTab === "automatic"
								? "var(--a-ink)"
								: "var(--a-ink-mute)",
						boxShadow:
							activeModuleTab === "automatic" ? "var(--a-shadow-sm)" : "none",
						whiteSpace: "nowrap",
					}}
				>
					<AdminIcon
						n="percent"
						s={14}
						c={
							activeModuleTab === "automatic"
								? "var(--a-terracotta)"
								: "var(--a-ink-mute)"
						}
					/>
					<span className="tn">
						Remises Automatiques ({autoKpis?.activeCount ?? 0} actives)
					</span>
				</button>

				<button
					type="button"
					onClick={() => setActiveModuleTab("flash")}
					className="active:scale-[0.96]"
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
						padding: "7px 14px",
						borderRadius: 6,
						fontSize: 12.5,
						fontWeight: activeModuleTab === "flash" ? 600 : 500,
						cursor: "pointer",
						border:
							activeModuleTab === "flash"
								? "1px solid var(--a-border)"
								: "1px solid transparent",
						transition:
							"color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
						background:
							activeModuleTab === "flash" ? "var(--a-surface)" : "transparent",
						color:
							activeModuleTab === "flash"
								? "var(--a-ink)"
								: "var(--a-ink-mute)",
						boxShadow:
							activeModuleTab === "flash" ? "var(--a-shadow-sm)" : "none",
						whiteSpace: "nowrap",
					}}
				>
					<AdminIcon
						n="zap"
						s={14}
						c={
							activeModuleTab === "flash"
								? "var(--a-terracotta)"
								: "var(--a-ink-mute)"
						}
					/>
					<span className="tn">
						Ventes Flash Programmées ({flashKpis?.activeCount ?? 0} en cours)
					</span>
				</button>
			</div>

			{/* SECTION 1 : CODES PROMO (6.1 & 6.2) */}
			{activeModuleTab === "coupons" && (
				<>
					<div className="kpi-grid">
						<div className="kpi">
							<div className="kpi-l">Codes actifs</div>
							<div className="kpi-v tn">{activeCount}</div>
							<div className="kpi-foot muted">En ligne</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Utilisations globales</div>
							<div className="kpi-v tn">{redemptionsCount}</div>
							<div className="kpi-foot muted">Cumulé</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Volume de remise accordé</div>
							<div className="kpi-v tn font-mono">
								{discountCount.toLocaleString("fr-FR")} F
							</div>
							<div className="kpi-foot muted">Remises totales</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Total codes créés</div>
							<div className="kpi-v tn">{totalCoupons}</div>
							<div className="kpi-foot muted">Catalogue</div>
						</div>
					</div>

					<Card className="mt-6">
						<div className="toolbar">
							<div className="flex items-center gap-1.5 overflow-x-auto">
								<ChipFilter
									on={statusFilter === ""}
									onClick={() => {
										setStatusFilter("");
										setPage(1);
									}}
								>
									Tous
								</ChipFilter>
								<ChipFilter
									on={statusFilter === "active"}
									onClick={() => {
										setStatusFilter("active");
										setPage(1);
									}}
								>
									Actifs
								</ChipFilter>
								<ChipFilter
									on={statusFilter === "scheduled"}
									onClick={() => {
										setStatusFilter("scheduled");
										setPage(1);
									}}
								>
									Programmés
								</ChipFilter>
								<ChipFilter
									on={statusFilter === "depleted"}
									onClick={() => {
										setStatusFilter("depleted");
										setPage(1);
									}}
								>
									Épuisés
								</ChipFilter>
								<ChipFilter
									on={statusFilter === "expired"}
									onClick={() => {
										setStatusFilter("expired");
										setPage(1);
									}}
								>
									Expirés
								</ChipFilter>
								<ChipFilter
									on={statusFilter === "inactive"}
									onClick={() => {
										setStatusFilter("inactive");
										setPage(1);
									}}
								>
									Inactifs
								</ChipFilter>
							</div>

							<div
								style={{
									marginLeft: "auto",
									position: "relative",
									display: "inline-flex",
									alignItems: "center",
								}}
							>
								<div
									style={{
										position: "absolute",
										left: 10,
										color: "var(--a-ink-mute)",
										pointerEvents: "none",
										display: "flex",
										alignItems: "center",
									}}
								>
									<AdminIcon n="search" s={13} />
								</div>
								<input
									type="text"
									placeholder="Rechercher un code promo..."
									value={search}
									onChange={(e) => {
										setSearch(e.target.value);
										setPage(1);
									}}
									className="aform-i"
									style={{
										paddingLeft: 28,
										paddingRight: search ? 28 : 10,
										paddingTop: 5,
										paddingBottom: 5,
										fontSize: 12.5,
										minWidth: 230,
									}}
								/>
								{search && (
									<button
										type="button"
										onClick={() => {
											setSearch("");
											setPage(1);
										}}
										style={{
											position: "absolute",
											right: 6,
											background: "none",
											border: "none",
											color: "var(--a-ink-mute)",
											cursor: "pointer",
											width: 24,
											height: 24,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
										className="hover:bg-[var(--a-surface-2)] hover:text-[var(--a-ink)] active:scale-[0.96] transition-all"
										title="Effacer la recherche"
									>
										<AdminIcon n="close" s={12} />
									</button>
								)}
							</div>
						</div>

						{isLoading ? (
							<div className="p-12 text-center text-xs text-slate-400">
								Chargement des codes promo...
							</div>
						) : isError ? (
							<div className="p-8 text-center text-xs text-red-500">
								Erreur lors du chargement des codes promo.
							</div>
						) : !data || data.items.length === 0 ? (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "48px 24px",
									textAlign: "center",
								}}
							>
								<div
									style={{
										width: 48,
										height: 48,
										borderRadius: "50%",
										background: "var(--a-surface-2)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginBottom: 12,
									}}
								>
									<AdminIcon n="tag" s={22} c="var(--a-ink-mute)" />
								</div>
								<div
									style={{
										fontSize: 13.5,
										fontWeight: 600,
										color: "var(--a-ink)",
									}}
								>
									Aucun code promo trouvé
								</div>
								<div
									style={{
										fontSize: 12,
										color: "var(--a-ink-mute)",
										marginTop: 4,
										marginBottom: 16,
									}}
								>
									{search || statusFilter
										? "Aucun résultat ne correspond à vos filtres actuels."
										: "Créez votre premier code de réduction pour booster vos ventes."}
								</div>
								{search || statusFilter ? (
									<AdminButton
										variant="secondary"
										size="sm"
										onClick={() => {
											setSearch("");
											setStatusFilter("");
											setPage(1);
										}}
									>
										Réinitialiser les filtres
									</AdminButton>
								) : (
									<AdminButton
										variant="primary"
										size="sm"
										onClick={handleOpenCreate}
									>
										<AdminIcon n="plus" s={14} />
										Créer un code promo
									</AdminButton>
								)}
							</div>
						) : (
							<div
								className="table-wrap"
								style={{ margin: 0, overflowX: "auto" }}
							>
								<table className="table">
									<thead>
										<tr>
											<th>CODE PROMO / INTITULÉ</th>
											<th>VALEUR</th>
											<th>CONDITIONS</th>
											<th>PÉRIODE</th>
											<th>UTILISATIONS</th>
											<th>STATUT</th>
											<th style={{ textAlign: "right" }}>ACTIONS</th>
										</tr>
									</thead>
									<tbody>
										{data.items.map((coupon) => {
											const st = computeDynamicStatus(coupon);
											const hasCat =
												Array.isArray(coupon.categoryIds) &&
												coupon.categoryIds.length > 0;
											const hasProd =
												Array.isArray(coupon.productIds) &&
												coupon.productIds.length > 0;
											const ratio = coupon.maxRedemptions
												? Math.min(
														100,
														Math.round(
															(coupon.redeemedCount / coupon.maxRedemptions) *
																100,
														),
													)
												: null;
											const barColor =
												ratio !== null
													? ratio >= 90
														? "var(--a-danger)"
														: ratio >= 50
															? "var(--a-warning)"
															: "var(--a-success)"
													: "var(--a-success)";

											return (
												<tr key={coupon.id}>
													<td>
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: 10,
															}}
														>
															<AdminAvatar name={coupon.code} />
															<div>
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: 6,
																	}}
																>
																	<span
																		className="mono font-bold"
																		style={{
																			fontSize: 13,
																			color: "var(--a-ink)",
																		}}
																	>
																		{coupon.code}
																	</span>
																	<button
																		type="button"
																		onClick={() => copyCode(coupon.code)}
																		title={
																			copiedCode === coupon.code
																				? "Copié !"
																				: "Copier le code"
																		}
																		style={{
																			padding: "3px 8px",
																			borderRadius: 4,
																			fontSize: 11,
																			minHeight: 24,
																			background:
																				copiedCode === coupon.code
																					? "var(--a-success-soft)"
																					: "var(--a-surface-2)",
																			color:
																				copiedCode === coupon.code
																					? "var(--a-success)"
																					: "var(--a-ink-soft)",
																			border: "1px solid var(--a-border)",
																			display: "inline-flex",
																			alignItems: "center",
																			gap: 4,
																			cursor: "pointer",
																			transition:
																				"color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease",
																		}}
																		className="active:scale-[0.96]"
																	>
																		<AdminIcon
																			n={
																				copiedCode === coupon.code
																					? "check"
																					: "tag"
																			}
																			s={11}
																			c={
																				copiedCode === coupon.code
																					? "var(--a-success)"
																					: "var(--a-ink-mute)"
																			}
																		/>
																		{copiedCode === coupon.code ? (
																			<span style={{ fontWeight: 600 }}>
																				Copié !
																			</span>
																		) : (
																			<span>Copier</span>
																		)}
																	</button>
																</div>
																{coupon.description && (
																	<div
																		className="muted"
																		style={{ fontSize: 11, marginTop: 2 }}
																	>
																		{coupon.description}
																	</div>
																)}
															</div>
														</div>
													</td>
													<td
														className="tn font-bold"
														style={{ fontSize: 13, color: "var(--a-ink)" }}
													>
														{coupon.type === "PERCENT"
															? `-${coupon.value}%`
															: `-${coupon.value.toLocaleString("fr-FR")} F`}
													</td>
													<td>
														{coupon.minSubtotal > 0 && (
															<div
																className="tn muted"
																style={{ fontSize: 11 }}
															>
																Min.{" "}
																{coupon.minSubtotal.toLocaleString("fr-FR")} F
															</div>
														)}
														{hasCat && (
															<Tag
																variant="ochre"
																noDot
																style={{ marginTop: 2 }}
															>
																Catégories
															</Tag>
														)}
														{hasProd && (
															<Tag
																variant="info"
																noDot
																style={{ marginTop: 2 }}
															>
																Produits
															</Tag>
														)}
														{!hasCat &&
															!hasProd &&
															coupon.minSubtotal === 0 && (
																<span className="muted">—</span>
															)}
													</td>
													<td
														className="muted font-medium"
														style={{ fontSize: 12 }}
													>
														{formatPeriod(coupon.startsAt, coupon.expiresAt)}
													</td>
													<td>
														<div
															className="tn font-bold"
															style={{ fontSize: 12.5 }}
														>
															{coupon.redeemedCount}{" "}
															{coupon.maxRedemptions
																? `/ ${coupon.maxRedemptions}`
																: "fois"}
														</div>
														{ratio !== null && (
															<div
																style={{
																	width: 70,
																	height: 4,
																	borderRadius: 2,
																	background: "var(--a-border)",
																	marginTop: 4,
																	overflow: "hidden",
																}}
															>
																<div
																	style={{
																		width: `${Math.max(4, ratio)}%`,
																		height: "100%",
																		background: barColor,
																		borderRadius: 2,
																	}}
																/>
															</div>
														)}
													</td>
													<td>
														<Tag variant={st.cls}>{st.label}</Tag>
													</td>
													<td style={{ textAlign: "right" }}>
														<div
															style={{
																display: "inline-flex",
																gap: 6,
																justifyContent: "flex-end",
															}}
														>
															<AdminButton
																variant="ghost"
																size="sm"
																onClick={() => handleToggleStatus(coupon)}
															>
																{coupon.isActive ? "Désactiver" : "Activer"}
															</AdminButton>
															<AdminButton
																variant="ghost"
																size="sm"
																onClick={() => handleOpenEdit(coupon)}
															>
																Éditer
															</AdminButton>
														</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
						{data && data.pages > 0 && (
							<Pagination
								from={data.items.length > 0 ? (page - 1) * 20 + 1 : 0}
								to={Math.min(page * 20, data.total)}
								total={data.total}
								current={page}
								totalPages={data.pages}
								onChange={setPage}
							/>
						)}
					</Card>
				</>
			)}

			{/* SECTION 2 : REMISES AUTOMATIQUES SANS CODE (6.3) */}
			{activeModuleTab === "automatic" && (
				<>
					<div className="kpi-grid">
						<div className="kpi">
							<div className="kpi-l">Offres automatiques actives</div>
							<div className="kpi-v tn">{autoKpis?.activeCount ?? 0}</div>
							<div className="kpi-foot muted">Au panier</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Offres programmées</div>
							<div className="kpi-v tn">{autoKpis?.scheduledCount ?? 0}</div>
							<div className="kpi-foot muted">À venir</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Total remises distribuées</div>
							<div className="kpi-v tn font-mono">
								{Number(autoKpis?.totalDiscountDistributed || 0).toLocaleString(
									"fr-FR",
								)}{" "}
								F
							</div>
							<div className="kpi-foot muted">Économies clients</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Total applications panier</div>
							<div className="kpi-v tn">{autoKpis?.totalRedemptions ?? 0}</div>
							<div className="kpi-foot muted">Volume</div>
						</div>
					</div>

					<Card className="mt-6">
						<div className="toolbar">
							<div className="flex items-center gap-1.5 overflow-x-auto">
								<ChipFilter
									on={autoStatusFilter === ""}
									onClick={() => {
										setAutoStatusFilter("");
										setAutoPage(1);
									}}
								>
									Toutes
								</ChipFilter>
								<ChipFilter
									on={autoStatusFilter === "active"}
									onClick={() => {
										setAutoStatusFilter("active");
										setAutoPage(1);
									}}
								>
									Actives
								</ChipFilter>
								<ChipFilter
									on={autoStatusFilter === "scheduled"}
									onClick={() => {
										setAutoStatusFilter("scheduled");
										setAutoPage(1);
									}}
								>
									Programmées
								</ChipFilter>
								<ChipFilter
									on={autoStatusFilter === "expired"}
									onClick={() => {
										setAutoStatusFilter("expired");
										setAutoPage(1);
									}}
								>
									Expirées
								</ChipFilter>
								<ChipFilter
									on={autoStatusFilter === "inactive"}
									onClick={() => {
										setAutoStatusFilter("inactive");
										setAutoPage(1);
									}}
								>
									Inactives
								</ChipFilter>
							</div>

							<div
								style={{
									marginLeft: "auto",
									position: "relative",
									display: "inline-flex",
									alignItems: "center",
								}}
							>
								<div
									style={{
										position: "absolute",
										left: 10,
										color: "var(--a-ink-mute)",
										pointerEvents: "none",
										display: "flex",
										alignItems: "center",
									}}
								>
									<AdminIcon n="search" s={13} />
								</div>
								<input
									type="text"
									placeholder="Rechercher une remise..."
									value={autoSearch}
									onChange={(e) => {
										setAutoSearch(e.target.value);
										setAutoPage(1);
									}}
									className="aform-i"
									style={{
										paddingLeft: 28,
										paddingRight: autoSearch ? 28 : 10,
										paddingTop: 5,
										paddingBottom: 5,
										fontSize: 12.5,
										minWidth: 230,
									}}
								/>
								{autoSearch && (
									<button
										type="button"
										onClick={() => {
											setAutoSearch("");
											setAutoPage(1);
										}}
										style={{
											position: "absolute",
											right: 6,
											background: "none",
											border: "none",
											color: "var(--a-ink-mute)",
											cursor: "pointer",
											width: 24,
											height: 24,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
										className="hover:bg-[var(--a-surface-2)] hover:text-[var(--a-ink)] active:scale-[0.96] transition-all"
										title="Effacer la recherche"
									>
										<AdminIcon n="close" s={12} />
									</button>
								)}
							</div>
						</div>

						{isAutoLoading ? (
							<div className="p-12 text-center text-xs text-slate-400">
								Chargement des remises automatiques...
							</div>
						) : isAutoError ? (
							<div className="p-8 text-center text-xs text-red-500">
								Erreur lors du chargement des remises automatiques.
							</div>
						) : !autoData || autoData.items.length === 0 ? (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "48px 24px",
									textAlign: "center",
								}}
							>
								<div
									style={{
										width: 48,
										height: 48,
										borderRadius: "50%",
										background: "var(--a-surface-2)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginBottom: 12,
									}}
								>
									<AdminIcon n="percent" s={22} c="var(--a-ink-mute)" />
								</div>
								<div
									style={{
										fontSize: 13.5,
										fontWeight: 600,
										color: "var(--a-ink)",
									}}
								>
									Aucune remise automatique programmée
								</div>
								<div
									style={{
										fontSize: 12,
										color: "var(--a-ink-mute)",
										marginTop: 4,
										marginBottom: 16,
									}}
								>
									{autoSearch || autoStatusFilter
										? "Aucun résultat ne correspond à vos filtres actuels."
										: "Créez une règle de remise automatique sans code promo au panier."}
								</div>
								{autoSearch || autoStatusFilter ? (
									<AdminButton
										variant="secondary"
										size="sm"
										onClick={() => {
											setAutoSearch("");
											setAutoStatusFilter("");
											setAutoPage(1);
										}}
									>
										Réinitialiser les filtres
									</AdminButton>
								) : (
									<AdminButton
										variant="primary"
										size="sm"
										onClick={handleOpenCreateAuto}
									>
										<AdminIcon n="plus" s={14} />
										Nouvelle remise automatique
									</AdminButton>
								)}
							</div>
						) : (
							<div
								className="table-wrap"
								style={{ margin: 0, overflowX: "auto" }}
							>
								<table className="table">
									<thead>
										<tr>
											<th>PRIORITÉ & OFFRE</th>
											<th>VALEUR REMISE</th>
											<th>CIBLE CLIENTS</th>
											<th>PÉRIODE</th>
											<th>APPLIQUÉE</th>
											<th>STATUT</th>
											<th style={{ textAlign: "right" }}>ACTIONS</th>
										</tr>
									</thead>
									<tbody>
										{autoData.items.map((discount) => {
											const st = computeAutoDynamicStatus(discount);

											return (
												<tr key={discount.id}>
													<td>
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: 10,
															}}
														>
															<AdminAvatar name={discount.name} />
															<div>
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: 6,
																	}}
																>
																	<Tag
																		variant="neutral"
																		noDot
																		style={{ fontSize: 10 }}
																	>
																		P{discount.priority}
																	</Tag>
																	<strong
																		style={{
																			fontSize: 13,
																			color: "var(--a-ink)",
																		}}
																	>
																		{discount.name}
																	</strong>
																</div>
																{discount.description && (
																	<div
																		className="muted"
																		style={{ fontSize: 11, marginTop: 2 }}
																	>
																		{discount.description}
																	</div>
																)}
															</div>
														</div>
													</td>
													<td
														className="tn font-bold"
														style={{ fontSize: 13, color: "var(--a-ink)" }}
													>
														{discount.type === "PERCENT"
															? `-${discount.value}%`
															: `-${discount.value.toLocaleString("fr-FR")} F`}
													</td>
													<td>
														<Tag variant="terra" noDot style={{ fontSize: 11 }}>
															{discount.customerScope === "ALL" &&
																"Tous les clients"}
															{discount.customerScope === "FIRST_ORDER" &&
																"1ère commande"}
															{discount.customerScope === "B2B" &&
																"Clients B2B"}
															{discount.customerScope === "B2C" &&
																"Clients B2C"}
														</Tag>
													</td>
													<td
														className="muted font-medium"
														style={{ fontSize: 12 }}
													>
														{formatPeriod(
															discount.startsAt,
															discount.expiresAt,
														)}
													</td>
													<td
														className="tn font-bold"
														style={{ fontSize: 12.5 }}
													>
														{discount.redeemedCount} fois
													</td>
													<td>
														<Tag variant={st.cls}>{st.label}</Tag>
													</td>
													<td style={{ textAlign: "right" }}>
														<div
															style={{
																display: "inline-flex",
																gap: 6,
																justifyContent: "flex-end",
															}}
														>
															<AdminButton
																variant="ghost"
																size="sm"
																onClick={() => handleToggleAutoStatus(discount)}
															>
																{discount.isActive ? "Désactiver" : "Activer"}
															</AdminButton>
															<AdminButton
																variant="ghost"
																size="sm"
																onClick={() => handleOpenEditAuto(discount)}
															>
																Éditer
															</AdminButton>
														</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
						{autoData && autoData.pages > 0 && (
							<Pagination
								from={autoData.items.length > 0 ? (autoPage - 1) * 20 + 1 : 0}
								to={Math.min(autoPage * 20, autoData.total)}
								total={autoData.total}
								current={autoPage}
								totalPages={autoData.pages}
								onChange={setAutoPage}
							/>
						)}
					</Card>
				</>
			)}

			{/* SECTION 3 : VENTES FLASH PROGRAMMÉES (6.4) */}
			{activeModuleTab === "flash" && (
				<>
					<div className="kpi-grid">
						<div className="kpi">
							<div className="kpi-l">Ventes flash en cours</div>
							<div className="kpi-v tn">{flashKpis?.activeCount ?? 0}</div>
							<div className="kpi-foot muted">Direct Boutique</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Ventes flash programmées</div>
							<div className="kpi-v tn">{flashKpis?.scheduledCount ?? 0}</div>
							<div className="kpi-foot muted">À venir</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Total articles vendus</div>
							<div className="kpi-v tn">{flashKpis?.totalSoldCount ?? 0}</div>
							<div className="kpi-foot muted">Articles écoulés</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Total campagnes flash</div>
							<div className="kpi-v tn">{flashKpis?.totalCount ?? 0}</div>
							<div className="kpi-foot muted">Historique</div>
						</div>
					</div>

					<Card className="mt-6">
						<div className="toolbar">
							<div className="flex items-center gap-1.5 overflow-x-auto">
								<ChipFilter
									on={flashStatusFilter === ""}
									onClick={() => {
										setFlashStatusFilter("");
										setFlashPage(1);
									}}
								>
									Toutes
								</ChipFilter>
								<ChipFilter
									on={flashStatusFilter === "active"}
									onClick={() => {
										setFlashStatusFilter("active");
										setFlashPage(1);
									}}
								>
									En cours
								</ChipFilter>
								<ChipFilter
									on={flashStatusFilter === "scheduled"}
									onClick={() => {
										setFlashStatusFilter("scheduled");
										setFlashPage(1);
									}}
								>
									Programmées
								</ChipFilter>
								<ChipFilter
									on={flashStatusFilter === "depleted"}
									onClick={() => {
										setFlashStatusFilter("depleted");
										setFlashPage(1);
									}}
								>
									Épuisées
								</ChipFilter>
								<ChipFilter
									on={flashStatusFilter === "expired"}
									onClick={() => {
										setFlashStatusFilter("expired");
										setFlashPage(1);
									}}
								>
									Expirées
								</ChipFilter>
								<ChipFilter
									on={flashStatusFilter === "inactive"}
									onClick={() => {
										setFlashStatusFilter("inactive");
										setFlashPage(1);
									}}
								>
									Inactives
								</ChipFilter>
							</div>

							<div
								style={{
									marginLeft: "auto",
									position: "relative",
									display: "inline-flex",
									alignItems: "center",
								}}
							>
								<div
									style={{
										position: "absolute",
										left: 10,
										color: "var(--a-ink-mute)",
										pointerEvents: "none",
										display: "flex",
										alignItems: "center",
									}}
								>
									<AdminIcon n="search" s={13} />
								</div>
								<input
									type="text"
									placeholder="Rechercher une vente flash..."
									value={flashSearch}
									onChange={(e) => {
										setFlashSearch(e.target.value);
										setFlashPage(1);
									}}
									className="aform-i"
									style={{
										paddingLeft: 28,
										paddingRight: flashSearch ? 28 : 10,
										paddingTop: 5,
										paddingBottom: 5,
										fontSize: 12.5,
										minWidth: 230,
									}}
								/>
								{flashSearch && (
									<button
										type="button"
										onClick={() => {
											setFlashSearch("");
											setFlashPage(1);
										}}
										style={{
											position: "absolute",
											right: 6,
											background: "none",
											border: "none",
											color: "var(--a-ink-mute)",
											cursor: "pointer",
											width: 24,
											height: 24,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
										className="hover:bg-[var(--a-surface-2)] hover:text-[var(--a-ink)] active:scale-[0.96] transition-all"
										title="Effacer la recherche"
									>
										<AdminIcon n="close" s={12} />
									</button>
								)}
							</div>
						</div>

						{isFlashLoading ? (
							<div className="p-12 text-center text-xs text-slate-400">
								Chargement des ventes flash...
							</div>
						) : isFlashError ? (
							<div className="p-8 text-center text-xs text-red-500">
								Erreur lors du chargement des ventes flash.
							</div>
						) : !flashData || flashData.items.length === 0 ? (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "48px 24px",
									textAlign: "center",
								}}
							>
								<div
									style={{
										width: 48,
										height: 48,
										borderRadius: "50%",
										background: "var(--a-surface-2)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginBottom: 12,
									}}
								>
									<AdminIcon n="zap" s={22} c="var(--a-ink-mute)" />
								</div>
								<div
									style={{
										fontSize: 13.5,
										fontWeight: 600,
										color: "var(--a-ink)",
									}}
								>
									Aucune vente flash programmée
								</div>
								<div
									style={{
										fontSize: 12,
										color: "var(--a-ink-mute)",
										marginTop: 4,
										marginBottom: 16,
									}}
								>
									{flashSearch || flashStatusFilter
										? "Aucun résultat ne correspond à vos filtres actuels."
										: "Programmez votre première vente flash d'urgence pour vos produits phares."}
								</div>
								{flashSearch || flashStatusFilter ? (
									<AdminButton
										variant="secondary"
										size="sm"
										onClick={() => {
											setFlashSearch("");
											setFlashStatusFilter("");
											setFlashPage(1);
										}}
									>
										Réinitialiser les filtres
									</AdminButton>
								) : (
									<AdminButton
										variant="primary"
										size="sm"
										onClick={handleOpenCreateFlash}
									>
										<AdminIcon n="plus" s={14} />
										Programmer une Vente Flash
									</AdminButton>
								)}
							</div>
						) : (
							<div
								className="table-wrap"
								style={{ margin: 0, overflowX: "auto" }}
							>
								<table className="table">
									<thead>
										<tr>
											<th>OPÉRATION & PRODUIT</th>
											<th>PRIX REGULIER</th>
											<th>PRIX FLASH</th>
											<th>STOCK & ÉCOULEMENT</th>
											<th>PÉRIODE</th>
											<th>STATUT</th>
											<th style={{ textAlign: "right" }}>ACTIONS</th>
										</tr>
									</thead>
									<tbody>
										{flashData.items.map((flashSale) => {
											const st = computeFlashDynamicStatus(flashSale);
											const prodTitle = flashSale.product?.title || "Produit";
											const basePrice = Number(
												flashSale.product?.basePrice || 0,
											);
											const ratio =
												flashSale.stockLimit > 0
													? Math.min(
															100,
															Math.round(
																(flashSale.soldCount / flashSale.stockLimit) *
																	100,
															),
														)
													: null;
											const barColor =
												ratio !== null
													? ratio >= 90
														? "var(--a-danger)"
														: ratio >= 50
															? "var(--a-warning)"
															: "var(--a-success)"
													: "var(--a-success)";

											return (
												<tr key={flashSale.id}>
													<td>
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: 10,
															}}
														>
															<AdminAvatar name={flashSale.name || prodTitle} />
															<div>
																<div
																	style={{
																		fontWeight: 700,
																		fontSize: 13,
																		color: "var(--a-ink)",
																	}}
																>
																	{flashSale.name}
																</div>
																<div
																	className="muted mono"
																	style={{ fontSize: 11, marginTop: 2 }}
																>
																	{prodTitle}{" "}
																	{flashSale.product?.sku
																		? `(${flashSale.product.sku})`
																		: ""}
																</div>
															</div>
														</div>
													</td>
													<td>
														{basePrice > 0 ? (
															<span
																className="tn muted line-through"
																style={{ fontSize: 12 }}
															>
																{basePrice.toLocaleString("fr-FR")} F
															</span>
														) : (
															<span className="muted">—</span>
														)}
													</td>
													<td
														className="tn font-bold"
														style={{ fontSize: 13, color: "var(--a-ink)" }}
													>
														{flashSale.flashPrice.toLocaleString("fr-FR")} F
													</td>
													<td>
														<div
															className="tn font-bold"
															style={{ fontSize: 12.5 }}
														>
															{flashSale.soldCount}{" "}
															{flashSale.stockLimit > 0
																? `/ ${flashSale.stockLimit}`
																: "unités"}
														</div>
														{ratio !== null && (
															<div
																style={{
																	width: 70,
																	height: 4,
																	borderRadius: 2,
																	background: "var(--a-border)",
																	marginTop: 4,
																	overflow: "hidden",
																}}
															>
																<div
																	style={{
																		width: `${Math.max(4, ratio)}%`,
																		height: "100%",
																		background: barColor,
																		borderRadius: 2,
																	}}
																/>
															</div>
														)}
													</td>
													<td
														className="muted font-medium"
														style={{ fontSize: 12 }}
													>
														{formatPeriod(
															flashSale.startsAt,
															flashSale.expiresAt,
														)}
													</td>
													<td>
														<Tag variant={st.cls}>{st.label}</Tag>
													</td>
													<td style={{ textAlign: "right" }}>
														<div
															style={{
																display: "inline-flex",
																gap: 6,
																justifyContent: "flex-end",
															}}
														>
															<AdminButton
																variant="ghost"
																size="sm"
																onClick={() =>
																	handleToggleFlashStatus(flashSale)
																}
															>
																{flashSale.isActive ? "Désactiver" : "Activer"}
															</AdminButton>
															<AdminButton
																variant="ghost"
																size="sm"
																onClick={() => handleOpenEditFlash(flashSale)}
															>
																Éditer
															</AdminButton>
															<AdminButton
																variant="ghost"
																size="sm"
																onClick={() => handleDeleteFlash(flashSale.id)}
																style={{ color: "var(--a-danger)" }}
															>
																Supprimer
															</AdminButton>
														</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
						{flashData && flashData.pages > 0 && (
							<Pagination
								from={flashData.items.length > 0 ? (flashPage - 1) * 20 + 1 : 0}
								to={Math.min(flashPage * 20, flashData.total)}
								total={flashData.total}
								current={flashPage}
								totalPages={flashData.pages}
								onChange={setFlashPage}
							/>
						)}
					</Card>
				</>
			)}

			<PromotionModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				initialData={editingCoupon}
				onSuccess={() => void refetch()}
			/>

			<AutomaticDiscountModal
				isOpen={isAutoModalOpen}
				onClose={() => setIsAutoModalOpen(false)}
				initialData={editingAutoDiscount}
				onSuccess={() => void refetchAuto()}
			/>

			<FlashSaleModal
				isOpen={isFlashModalOpen}
				onClose={() => setIsFlashModalOpen(false)}
				initialData={editingFlashSale}
				onSuccess={() => void refetchFlash()}
			/>
		</>
	);
}
